import type { EconomyConfig } from '../config/types';
import { now } from './clock';

export type Rarity = 0 | 1 | 2 | 3 | 4;
export type GearSlot = 'tool' | 'helmet' | 'vest' | 'pack';

export interface ItemRef {
  itemId: string;
  rarity: Rarity;
}

export interface ItemStack extends ItemRef {
  count: number;
}

export interface StationState {
  level: number;
  manager: boolean;
  pile: number;
  cycleEndsAt: number;
  abilityEndsAt: number;
  abilityReadyAt: number;
}

export type ShaftState = StationState;

export interface ExpeditionEventState {
  id: string;
  atMs: number;
  choice: 'safe' | 'bold' | null;
  success?: boolean;
}

export interface ExpeditionState {
  typeId: string;
  startedAt: number;
  endsAt: number;
  zoneTier: number;
  events: ExpeditionEventState[];
  doubled: boolean;
}

export interface GameState {
  schemaVersion: number;
  createdAt: number;
  lastSavedAt: number;
  currencies: { gold: number; gems: number; glowstones: number };
  site: {
    index: number;
    shafts: ShaftState[];
    elevator: StationState;
    warehouse: StationState;
    surfacePile: number;
    earnedThisSite: number;
    enteredAt: number;
  };
  shrine: Record<string, number>;
  boosts: { adBoostEndsAt: number };
  entitlements: { adFree: boolean; goldenLantern: boolean; starterPack: boolean };
  extraOfflineHoursPurchased: number;
  daily: {
    lastClaimDay: string | null;
    streakDay: number;
    gemChestUsesToday: number;
    adViewsToday: number;
    dayKey: string;
  };
  tutorial: { step: number; done: boolean; tipsSeen: string[] };
  achievements: string[];
  settings: {
    music: boolean;
    sfx: boolean;
    haptics: boolean;
    notation: 'letters' | 'scientific';
  };
  stats: { lifetimeGold: number; migrations: number; deepestEver: number };
  rngSeed: number;
  hero: {
    name: string;
    level: number;
    xp: number;
    equipped: Record<GearSlot, ItemRef | null>;
    bag: ItemStack[];
    expedition: ExpeditionState | null;
  };
  managers: Record<string, { badges: number; level: number }>;
  guardians: {
    defeated: boolean[];
    failedAttempts: number[];
    retryAt: number[];
  };
  veins: { banked: number; nextAt: number };
  jobs: {
    dayKey: string;
    list: { id: string; target: number; progress: number; claimed: boolean }[];
    weeklyCount: number;
    weekKey: string;
  };
  incomeSamples: { dayKey: string; incomePerSec: number }[];
  camp: null;
}

function emptyStation(level: number): StationState {
  return {
    level,
    manager: false,
    pile: 0,
    cycleEndsAt: 0,
    abilityEndsAt: 0,
    abilityReadyAt: 0,
  };
}

function starterEquipped(e: EconomyConfig): Record<GearSlot, ItemRef | null> {
  const slots: GearSlot[] = ['tool', 'helmet', 'vest', 'pack'];
  const out: Record<GearSlot, ItemRef | null> = {
    tool: null,
    helmet: null,
    vest: null,
    pack: null,
  };
  const st = e.gear.starterItem;
  const slot = st.slot as GearSlot;
  if (slots.includes(slot)) {
    out[slot] = {
      itemId: `${slot}_t${st.tier}`,
      rarity: st.rarity as Rarity,
    };
  }
  return out;
}

function initialShrine(e: EconomyConfig): Record<string, number> {
  const shrine: Record<string, number> = {};
  for (const u of e.shrine.upgrades) {
    shrine[u.id] = 0;
  }
  return shrine;
}

/** Fresh install state from economy.json start + schemaVersion. */
export function createInitialState(e: EconomyConfig, atMs: number = now()): GameState {
  const save = e.save as { schemaVersion?: number } | undefined;
  const schemaVersion = save?.schemaVersion;
  if (typeof schemaVersion !== 'number') {
    throw new Error('economy.json save.schemaVersion required');
  }
  const start = e.start as { gold?: number; gems?: number; unlockedShafts?: number };
  if (typeof start?.gold !== 'number' || typeof start?.gems !== 'number') {
    throw new Error('economy.json start.gold/gems required');
  }
  const unlocked = typeof start.unlockedShafts === 'number' ? start.unlockedShafts : 1;
  const shaftCount = e.shafts.count;
  const shafts: ShaftState[] = [];
  for (let i = 0; i < shaftCount; i++) {
    shafts.push(emptyStation(i < unlocked ? 1 : 0));
  }
  const guardianCount = e.guardians.shafts.length;

  return {
    schemaVersion,
    createdAt: atMs,
    lastSavedAt: atMs,
    currencies: {
      gold: start.gold,
      gems: start.gems,
      glowstones: 0,
    },
    site: {
      index: 0,
      shafts,
      elevator: emptyStation(1),
      warehouse: emptyStation(1),
      surfacePile: 0,
      earnedThisSite: 0,
      enteredAt: atMs,
    },
    shrine: initialShrine(e),
    boosts: { adBoostEndsAt: 0 },
    entitlements: { adFree: false, goldenLantern: false, starterPack: false },
    extraOfflineHoursPurchased: 0,
    daily: {
      lastClaimDay: null,
      streakDay: 0,
      gemChestUsesToday: 0,
      adViewsToday: 0,
      dayKey: '',
    },
    tutorial: { step: 0, done: false, tipsSeen: [] },
    achievements: [],
    settings: { music: true, sfx: true, haptics: true, notation: 'letters' },
    stats: { lifetimeGold: 0, migrations: 0, deepestEver: unlocked },
    rngSeed: 1,
    hero: {
      name: e.hero.defaultName,
      level: 1,
      xp: 0,
      equipped: starterEquipped(e),
      bag: [],
      expedition: null,
    },
    managers: {},
    guardians: {
      defeated: Array.from({ length: guardianCount }, () => false),
      failedAttempts: Array.from({ length: guardianCount }, () => 0),
      retryAt: Array.from({ length: guardianCount }, () => 0),
    },
    veins: { banked: 0, nextAt: 0 },
    jobs: { dayKey: '', list: [], weeklyCount: 0, weekKey: '' },
    incomeSamples: [],
    camp: null,
  };
}

/** Hero is home when no expedition is in progress. */
export function isHeroHome(state: GameState, atMs: number = now()): boolean {
  const ex = state.hero.expedition;
  if (!ex) return true;
  return atMs >= ex.endsAt;
}
